/* Keep native product/variant/cart handlers; adapt only desktop dialog presentation. */
(() => {
  'use strict';
  if (window.vnaDesktopQuickDrawer) return;
  window.vnaDesktopQuickDrawer = true;
  const selector = '[data-vna-quick-modal], [data-vna-showcase-quick-modal]';
  const desktop = matchMedia('(min-width: 750px)');
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const states = new Map();
  let openingTrigger = null;
  // Safari does not focus a pointer-clicked button, so remember the actual opener.
  document.addEventListener('click', event => {
    if (!desktop.matches) return;
    const trigger = event.target.closest('[data-vna-quick-open], [data-vna-showcase-quick-open]');
    if (trigger) openingTrigger = trigger;
  }, true);
  const duration = () => reduced.matches ? 0 : 320;
  const lock = () => document.documentElement.classList.toggle('vna-desktop-quick-lock',
    desktop.matches && [...states.values()].some(state => state.open || state.closing));
  const cancel = state => {
    cancelAnimationFrame(state.frame);
    clearTimeout(state.timer);
  };
  const focusables = modal => [...(modal.querySelector('[role="dialog"]') || modal).querySelectorAll('button, a[href], input, select, textarea, [tabindex]')]
    .filter(el => !el.disabled && el.tabIndex >= 0 && !el.closest('[inert]') && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');

  function sync(modal, state) {
    if (!desktop.matches) {
      cancel(state);
      state.open = state.closing = false;
      modal.classList.remove('is-desktop-quick-open', 'is-desktop-quick-closing');
      modal.removeAttribute('data-vna-desktop-quick');
      modal.inert = state.originalInert;
      lock();
      return;
    }
    modal.setAttribute('data-vna-desktop-quick', '');
    const open = modal.getAttribute('aria-hidden') === 'false';
    if (open && !state.open) {
      const reopening = state.closing;
      cancel(state);
      state.open = true;
      state.closing = false;
      if (reopening) modal.classList.add('is-desktop-quick-open');
      modal.classList.remove('is-desktop-quick-closing');
      state.trigger = openingTrigger;
      openingTrigger = null;
      modal.inert = false;
      lock();
      // Native showcase uses hidden: render the offscreen panel before starting its slide.
      state.frame = requestAnimationFrame(() => {
        state.frame = requestAnimationFrame(() => {
          if (!state.open || !desktop.matches) return;
          modal.classList.add('is-desktop-quick-open');
          const options = modal.querySelector('.vna-quick-options, .vna-showcase-quick-options');
          if (options) options.scrollTop = 0;
          const target = modal.querySelector('.vna-quick-option-value:not([disabled]), .vna-showcase-quick-option-value:not([disabled])') || focusables(modal)[0];
          target?.focus({ preventScroll: true });
        });
      });
    } else if (!open && state.open) {
      cancel(state);
      state.open = false;
      state.closing = true;
      modal.inert = true;
      modal.classList.add('is-desktop-quick-closing');
      modal.classList.remove('is-desktop-quick-open');
      if (!document.body.matches('.vna-cart-drawer-open, .cart-sidebar-show') && state.trigger?.isConnected && state.trigger.getClientRects().length) {
        state.trigger.focus({ preventScroll: true });
      }
      state.timer = setTimeout(() => {
        state.closing = false;
        modal.classList.remove('is-desktop-quick-closing');
        lock();
      }, duration());
    } else if (!open) {
      modal.inert = true;
    }
  }

  function register(root) {
    const modals = root.matches?.(selector) ? [root] : [...root.querySelectorAll(selector)];
    for (const modal of modals) {
      if (states.has(modal)) continue;
      const state = { open: false, closing: false, originalInert: modal.inert, frame: 0, timer: 0 };
      states.set(modal, state);
      sync(modal, state);
    }
  }

  register(document);
  new MutationObserver(records => {
    for (const record of records) {
      if (record.type === 'attributes' && states.has(record.target)) sync(record.target, states.get(record.target));
      for (const node of record.addedNodes) if (node.nodeType === 1) register(node);
    }
    for (const [modal, state] of states) if (!modal.isConnected) {
      cancel(state);
      states.delete(modal);
      lock();
    }
  }).observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ['aria-hidden', 'hidden'] });

  desktop.addEventListener('change', () => {
    for (const [modal, state] of states) sync(modal, state);
  });
  document.addEventListener('keydown', event => {
    if (!desktop.matches || event.key !== 'Tab') return;
    const modal = [...states.keys()].find(el => el.getAttribute('aria-hidden') === 'false');
    if (!modal) return;
    const items = focusables(modal);
    if (!items.length) return;
    const first = items[0], last = items[items.length - 1];
    if (event.shiftKey && (document.activeElement === first || !items.includes(document.activeElement))) {
      event.preventDefault(); last.focus({ preventScroll: true });
    } else if (!event.shiftKey && (document.activeElement === last || !items.includes(document.activeElement))) {
      event.preventDefault(); first.focus({ preventScroll: true });
    }
  }, true);
})();
